import type { TeammatePlaybookKnowledgeBaseAdherenceScore } from "@repo/app-types/entities/teammatePerformanceSummary";
import { Size } from "@repo/ui/helpers/helpers";
import { type FC, useMemo } from "react";
import { AverageCountPill } from "./averageCountPill.js";
import { computePassRatePillColor } from "./gradingPassRatePill.js";
import { PercentagePill, renderPercentage } from "./percentagePill.js";
import { TrendChart } from "./trendChart.js";

export interface KnowledgeBaseAdherenceStatsCardProps {
  data: TeammatePlaybookKnowledgeBaseAdherenceScore | null | undefined;
  isLoading?: boolean;
}

export const KnowledgeBaseAdherenceStatsCard: FC<
  KnowledgeBaseAdherenceStatsCardProps
> = (props) => {
  const incorrectCallRatio = props.data?.score.avg_incorrect_call_ratio;
  const incorrectCallRatioTrend =
    props.data?.score_trend.avg_incorrect_call_ratio_sparkline;

  const correctCallRatio =
    incorrectCallRatio !== undefined ? 1 - incorrectCallRatio : undefined;
  const correctCallRatioTrendData = useMemo(
    () =>
      incorrectCallRatioTrend?.map((score) =>
        score === null ? null : 1 - score,
      ),
    [incorrectCallRatioTrend],
  );

  const incorrectStatementsPerCall =
    props.data?.score.avg_incorrect_statements_per_call;
  const incorrectStatementsPerCallTrend =
    props.data?.score_trend.avg_incorrect_statements_per_call_sparkline;

  return (
    <div className="grid grid-cols-2 gap-6 rounded-lg border border-border-light bg-white p-4">
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-1.5">
          <div className="text-xs font-semibold text-foreground">
            Correct calls
          </div>

          <PercentagePill
            size={Size.SMALL}
            value={correctCallRatio}
            isLoading={props.isLoading}
            computeColor={computePassRatePillColor}
          />
        </div>

        {correctCallRatioTrendData ? (
          <TrendChart
            className="h-10"
            minYRange={0.01}
            showsXAxisLabels
            showsYAxisLabels
            lines={[
              {
                key: "correctCallRatio",
                data: correctCallRatioTrendData,
              },
            ]}
            renderValue={renderPercentage}
          />
        ) : null}
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-1.5">
          <div className="text-xs font-semibold text-foreground">
            Inaccuracies per call
          </div>

          <AverageCountPill
            size={Size.SMALL}
            value={incorrectStatementsPerCall}
            isLoading={props.isLoading}
            renderCount={renderIncorrectStatementCount}
          />
        </div>

        {incorrectStatementsPerCallTrend ? (
          <TrendChart
            className="h-10"
            minYRange={0.01}
            showsXAxisLabels
            showsYAxisLabels
            lines={[
              {
                key: "incorrectStatementsPerCall",
                data: incorrectStatementsPerCallTrend,
              },
            ]}
            renderValue={renderIncorrectStatementCount}
            getColorClassName={() => "text-orange-500"}
          />
        ) : null}
      </div>
    </div>
  );
};

function renderIncorrectStatementCount(value: number) {
  return value.toFixed(2);
}
